/**
 * Normalizers — helpers para tolerar variações de shape entre backend e frontend.
 *
 * Use ao consumir endpoints onde:
 * - Backend pode retornar com envelope (`{status, message, data}`) ou direto.
 * - Field names diferem entre DTO backend e tipos frontend (ex.: `description` vs `reason`).
 * - Arrays vêm sob nomes variáveis (`items`, `rows`, `users`).
 *
 * Aplicar SEMPRE no boundary (`contexts/*\/utils.ts`), nunca nos componentes.
 */

/**
 * Strip envelope `{status, message, data}` se presente. Retorna o conteúdo de `data`
 * ou o próprio objeto se já vier sem envelope.
 *
 * @example
 *   const data = unwrapEnvelope(response.data);
 *   // {status:"success", data:{users:[]}} → {users:[]}
 *   // {users:[]}                         → {users:[]}
 */
export function unwrapEnvelope<T = unknown>(raw: unknown): T {
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if ("data" in obj && typeof obj.status === "string") {
      return obj.data as T;
    }
  }
  return raw as T;
}

/**
 * Busca array sob múltiplos field names possíveis. Retorna o primeiro encontrado
 * que seja Array, ou [] como fallback.
 *
 * @example
 *   pickArray(data, ["reasons", "rows", "items"])
 *   // {reasons:[1,2]}      → [1,2]
 *   // {rows:[1,2]}         → [1,2]
 *   // {}                   → []
 */
export function pickArray<T = unknown>(
  source: unknown,
  keys: readonly string[]
): T[] {
  if (Array.isArray(source)) return source as T[];
  if (!source || typeof source !== "object") return [];
  const obj = source as Record<string, unknown>;
  for (const k of keys) {
    if (Array.isArray(obj[k])) return obj[k] as T[];
  }
  return [];
}

/**
 * Lê um valor sob múltiplos field names possíveis (primeiro definido vence).
 *
 * @example
 *   pickField(reason, ["description", "reason", "name"])
 *   // {description:"X"}     → "X"
 *   // {reason:"Y"}          → "Y"
 *   // {}                    → undefined
 */
export function pickField<T = unknown>(
  source: unknown,
  keys: readonly string[]
): T | undefined {
  if (!source || typeof source !== "object") return undefined;
  const obj = source as Record<string, unknown>;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k] as T;
  }
  return undefined;
}

/**
 * Mapeia uma lista aplicando aliases de field. Útil pra renomear campos
 * de backend pra shape esperado pelo frontend.
 *
 * @example
 *   renameFieldsInList(reasons, { reason: ["description", "reason"], id: ["id"] })
 *   // [{description:"X", id:1}] → [{reason:"X", id:1}]
 */
export function renameFieldsInList<TOut extends Record<string, unknown>>(
  list: unknown[],
  fieldMap: Record<keyof TOut, readonly string[]>
): TOut[] {
  return list.map((item) => {
    const out = {} as Record<string, unknown>;
    for (const key of Object.keys(fieldMap) as Array<keyof TOut>) {
      out[key as string] = pickField(item, fieldMap[key]);
    }
    return out as TOut;
  });
}
